import AppPage from "../AppPage";
import MenuBottomBar, { MenuBarItem } from "../MenuBottomBar";
import PlayerRankTotalPointsComponent from "../PlayerRankTotalPointsComponent";
import globals from "../../styles/globals.module.css";
import Logo from "../Logo";
import { vinyl_Icon, sparkle_Icon, help_Icon } from "../../assets/icons";
import { utilities } from "../../styles";
import { images } from "../../styles";
import { crown_Icon } from "../../assets/icons";
import { useActiveDraft, useCurrentDraft } from "../../hooks/drafts";
import { useCurrentPlayer, usePlayerStats } from "../../hooks/player";
import { useUserId } from "../../hooks/useUserId";
import { useMemo } from "react";
import { useNavigate } from "../../hooks/useNavigate";
import { Page } from "../../types";

function WelcomePage(props: { handleContinue: () => void }) {
  const { handleContinue } = props;
  const { navigate } = useNavigate();
  const { data: playerDetails, isPending } = useCurrentPlayer();
  const { data: activeDraft, isPending: isDraftCheckPending } =
    useActiveDraft();
  const { data: currentDraft } = useCurrentDraft();
  const userId = useUserId();
  const { data: playerStats, isPending: isStatsLoading } =
    usePlayerStats(userId);

  const playerWorldRank = useMemo(() => {
    if (!currentDraft?.leaderboardGroupId || !playerStats) {
      return -1;
    }
    return (
      playerStats.ranks.find(
        (rank) => rank.groupId === currentDraft.leaderboardGroupId,
      )?.rank ?? -1
    );
  }, [currentDraft, playerStats]);

  function renderActiveDraftContent() {
    if (isDraftCheckPending || !activeDraft) {
      return (
        <div className={utilities["flex-row"]}>
          <span className={globals["extra-lrg-btn--vinyl"]}>Loading...</span>
        </div>
      );
    }
    if (activeDraft.active === false && activeDraft.nextDraft) {
      return (
        <div className={utilities["flex-col"]}>
          <span className={globals["gameweek-header"]}>
            The next draft starts on:{" "}
            {new Date(activeDraft.nextDraft.startDate).toDateString()}
          </span>
          <span className={globals["gameweek_details"]}>
            And will end on:{" "}
            {new Date(activeDraft.nextDraft.endDate).toDateString()}
          </span>
        </div>
      );
    }
    if (activeDraft.active === false && activeDraft.prevDraft) {
      return (
        <div className={utilities["flex-col"]}>
          <span className={globals["gameweek-header"]}>
            The previous draft started on:{" "}
            {new Date(activeDraft.prevDraft.startDate).toDateString()}
          </span>
          <span className={globals["gameweek_details"]}>
            And ended on:{" "}
            {new Date(activeDraft.prevDraft.endDate).toDateString()}
          </span>
          <span className={globals["gameweek_details"]}>
            Visit the leaderboard to see how you did!
          </span>
        </div>
      );
    }
    if (activeDraft.active === false) {
      return (
        <div className={utilities["flex-col"]}>
          <span className={globals["gameweek-header"]}>
            The next draft has not been set yet.
          </span>
          <span className={globals["gameweek_details"]}>
            Check back soon to find out when it will be starting!
          </span>
        </div>
      );
    }
    if (activeDraft.areLineupsPending) {
      return (
        <div className={utilities["flex-col"]}>
          <span className={globals["gameweek-header"]}>
            We are currently updating artist data.
          </span>
          <span className={globals["gameweek_details"]}>
            Please check back soon!
          </span>
        </div>
      );
    }
    return (
      <>
        <button className={globals["button-wrapper"]} onClick={handleContinue}>
          <div
            className={`${globals["extra-lrg-btn--vinyl"]} ${utilities["flex-gap-20"]}`}
          >
            <img
              src={vinyl_Icon}
              className={images["vinyl-icon-flipped"]}
              alt=""
            />
            View my <br></br>Lineups!
            <img src={vinyl_Icon} className={images["vinyl-icon"]} alt="" />
          </div>
        </button>
        <div className={utilities["flex-row"]}>
          <img src={sparkle_Icon} className={images["sparkles-icon"]} alt="" />
          <span className={globals["score-text"]}>
            Total Lineup Score:{" "}
            {isStatsLoading || !playerStats
              ? "Loading..."
              : playerStats.totalScore}
          </span>
          <img src={sparkle_Icon} className={images["sparkles-icon"]} alt="" />
        </div>
        <div className={utilities["flex-col"]}>
          <span className={globals["gameweek-header"]}>
            Game Week: {activeDraft.currentWeek}
          </span>
          <span className={globals["gameweek_details"]}>
            Deadline: {activeDraft.nextDeadline}
          </span>
        </div>
      </>
    );
  }

  return (
    <AppPage
      bottomBar={
        <MenuBottomBar
          items={[
            MenuBarItem.News,
            MenuBarItem.Leaderboards,
            MenuBarItem.Groups,
            MenuBarItem.LogOut,
          ]}
        />
      }
      className={`${globals["page"]} ${globals["welcome-page"]}`}
      style={{ justifyContent: "flex-start" }}
    >
      <div className={globals["page-scroll-wrapper"]}>
        <div className={globals["page-container"]}>
          <div
            className={`${utilities["flex-row"]} ${utilities["width-100"]}`}
            style={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <div
              className={globals["rank-container"]}
              style={{
                position: "relative",
                top: "auto",
                left: "auto",
                margin: 0,
              }}
            >
              {!isStatsLoading && playerStats && (
                <>
                  <img
                    src={crown_Icon}
                    alt=""
                    className={images["crown-icon"]}
                  />
                  <PlayerRankTotalPointsComponent
                    totalPoints={playerStats.totalScore ?? 0}
                    globalRank={playerWorldRank}
                  />
                </>
              )}
            </div>
            <div
              className={`${utilities["flex-col"]} ${utilities["flex-center"]}`}
            >
              <button
                className={globals["button-wrapper"]}
                onClick={() => navigate(Page.HowToPlay)}
              >
                <div className={globals["help-btn"]}>
                  <img src={help_Icon} alt="" className={images["help-icon"]} />
                </div>
              </button>
              <span className={globals["help-btn-label"]}>How do I play?</span>
            </div>
          </div>
          {isPending ? (
            <span className={globals["welcome-header"]}>Loading...</span>
          ) : (
            <span className={globals["welcome-header"]}>
              Welcome Back, <br></br>
              {playerDetails?.username ?? "Player"}!
            </span>
          )}

          <Logo />
          {renderActiveDraftContent()}
        </div>
      </div>
    </AppPage>
  );
}

export default WelcomePage;
